import type React from "react";

import SuperError from "super-error";

import * as BusbudError from "@busbud/busbud-errors";

import type { SentryClient } from "@app/services/sentry-facade";
import type { TranslatorService } from "@app/services/translator";
import { helpUrl } from "@app/services/url-builders/company-pages-url-builder";

import type { SupportedLocale } from "@config/locales";
import type { AxiosError, AxiosResponse } from "axios";

interface ErrorWithCause extends Error {
  cause?: ErrorWithCause;
  errors?: unknown[];
  rootCause?: ErrorWithCause;
}
interface AxiosErrorWithCause extends Omit<AxiosError, "config"> {
  cause?: ErrorWithCause;
}

interface ErrorWithQuestionKey extends ErrorWithCause {
  question_key?: string;
}

export type ErrorHandlingAction =
  | "results_reload"
  | "checkout_restart_search"
  | "checkout_try_credit_card"
  | "checkout_change_credit_card"
  | "checkout_try_later"
  | "checkout_more_info"
  | "checkout_update_seat"
  | "checkout_update_hes"
  | "checkout_update_loyalty"
  | "reset_discount_code"
  | "reset_ancillary_items"
  | "reset_redeemed_amount"
  | null;

export interface AlertBoxButton {
  label: string;
  action_name?: ErrorHandlingAction;
}
export interface AlertBoxButtonConfig {
  cta_button?: AlertBoxButton;
  primary_button?: AlertBoxButton;
  secondary_button?: AlertBoxButton;
  className?: string;
}
export interface AlertBoxAlert {
  title: string;
  message: string;
  dismissHandler?: () => void;
  actionHandler?: (
    action_name: ErrorHandlingAction,
    error_button_type: ErrorModalButtonType
  ) => void;
  button_config?: AlertBoxButtonConfig;
  modal_config?: AlertBoxModalConfig;
  tip?: string | null;
}
export type AlertBoxShowAlert = (
  title: string | false,
  message: React.ReactNode,
  dismissHandler?: () => void,
  actionHandler?: (
    action_name: ErrorHandlingAction,
    error_button_type: ErrorModalButtonType
  ) => void,
  button_config?: AlertBoxButtonConfig,
  modal_config?: AlertBoxModalConfig
) => void;
export interface AlertBoxModalConfig {
  onClickAnywhere?: (event?: React.SyntheticEvent<HTMLElement>) => void;
}

export type ErrorModalButtonType = "cta" | "primary" | "secondary";

export interface ErrorModalConfig {
  name: string;
  title: string;
  text: string;
  tip?: string;
  button_config?: AlertBoxButtonConfig;
}

function defineApiError(
  name: string,
  default_message: string
): SuperError.SuperErrorI {
  return SuperError.subclass(name, function (message) {
    this.name = name;
    this.message = message || default_message;
    this.is_api_error = true;
    this.is_busbud_error = false;
  });
}

export const NetworkError = defineApiError(
  "NetworkError",
  "Client failed to connect to the network"
);

export const RequestAbortedError = defineApiError(
  "RequestAbortedError",
  "Browser cancelled the request"
);

export const TimeoutError = defineApiError(
  "TimeoutError",
  "Request timeout exceeded"
);

export const ServerError = defineApiError(
  "ServerError",
  "Server failed to fulfill the request"
);

export const ApiError = defineApiError(
  "ApiError",
  "An error occured during the API request"
);

export const ERROR_HANDLING_ACTIONS = {
  RESULTS_RELOAD: "results_reload",
  CHECKOUT_RESTART_SEARCH: "checkout_restart_search",
  CHECKOUT_TRY_CREDIT_CARD: "checkout_try_credit_card",
  CHECKOUT_CHANGE_CREDIT_CARD: "checkout_change_credit_card",
  CHECKOUT_TRY_LATER: "checkout_try_later",
  CHECKOUT_MORE_INFO: "checkout_more_info",
  CHECKOUT_UPDATE_SEAT: "checkout_update_seat",
  CHECKOUT_UPDATE_HES: "checkout_update_hes",
  CHECKOUT_UPDATE_LOYALTY: "checkout_update_loyalty",
  RESET_DISCOUNT_CODE: "reset_discount_code",
  RESET_ANCILLARY_ITEMS: "reset_ancillary_items",
  RESET_REDEEMED_AMOUNT: "reset_redeemed_amount"
} as const;

export function toMaybeApiError(
  err: (Error | AxiosError) & { transaction?: unknown }
) {
  let api_err;

  if (isAxiosBusbudError(err) && "response" in err) {
    api_err = fromPlainObject(err.response?.data.error, err);
    api_err.is_busbud_error = true;
  } else if (isAxiosNetworkError(err)) {
    api_err = new NetworkError();
  } else if (isAxiosRequestAbortedError(err)) {
    api_err = new RequestAbortedError();
  } else if (isAxiosTimeoutError(err)) {
    api_err = new TimeoutError(err.message);
  } else if (isAxiosServerError(err)) {
    const message =
      typeof err.response?.data === "string"
        ? err.response.data.slice(0, 100)
        : undefined;
    api_err = new ServerError(message);
  } else if (isAxiosError(err)) {
    api_err = new ApiError(err.message);
  }

  if (!api_err) {
    return;
  }

  api_err.is_api_error = true;
  api_err.is_busbud_error = api_err.is_busbud_error || false;
  api_err.status_code =
    "response" in err && err.response ? err.response.status : undefined;
  api_err.transaction =
    "transaction" in err && err.transaction ? err.transaction : undefined;
  api_err = safeCausedBy(api_err as BusbudError.BusbudError, err);

  return api_err;
}

export function toBusbudError(
  err: Error | null
): BusbudError.BusbudError | null {
  if (!err) {
    return null;
  }

  if (isBusbudError(err)) {
    return err;
  }

  if (isAxiosError(err)) {
    return fromAxiosError(err);
  }

  return fromPlainObject(err);
}

export function isBusbudError(err: Error): err is BusbudError.BusbudError {
  return err instanceof BusbudError.BusbudError;
}

export function getErrorReportFallback(
  err: AxiosError<{ message: string }[]>
): string | undefined {
  return err.response?.data && err.response?.data.map(d => d.message).join(",");
}

export function isAxiosBusbudError(
  err: Error
): err is AxiosError & { response: AxiosResponse } {
  return !!getErrorTypeFromNapi(err);
}

export function getErrorTypeFromNapi(
  err: Error | AxiosError<{ error?: { type?: string } }>
): string | undefined {
  if ("response" in err) {
    const { response } = err;
    return (
      response &&
      response.data &&
      response.data.error &&
      response.data.error.type
    );
  } else {
    return undefined;
  }
}

// Axios creates special errors if user's browser
// has connectivity issues during the request
//
// See: https://github.com/axios/axios/blob/master/lib/adapters/xhr.js

export function isAxiosNetworkError(
  err: Error | AxiosError
): err is AxiosError {
  return !!(err.message && err.message === "Network Error");
}

export function isAxiosRequestAbortedError(
  err: Error | AxiosError
): err is AxiosError {
  return !!(err.message && err.message === "Request aborted");
}

export function isAxiosTimeoutError(
  err: Error | AxiosError
): err is AxiosError {
  return !!(err.message && /^timeout of (\d+)ms exceeded/.test(err.message));
}

export function isAxiosServerError(err: Error | AxiosError): err is AxiosError {
  if ("response" in err) {
    const { response } = err;
    return !!(
      response &&
      response.status &&
      response.status >= 500 &&
      response.status < 600
    );
  }
  return false;
}

export function isAxiosConnectionRefusedError(err: AxiosError): boolean {
  if ("code" in err) {
    return !!err.code?.includes("ECONNREFUSED");
  }
  return false;
}

function isAxiosError(
  err: Error | AxiosError
): err is AxiosError<{ error?: { details?: string } }> {
  return (
    "response" in err &&
    !!err.response &&
    !!err.response.data &&
    !!err.response.status
  );
}

/**
 * Utilities to set cause on a busbud error.
 * Because axios errors are not Error instance
 *
 * @param bb_err
 * @param cause_err
 * @returns
 */
export function safeCausedBy<T extends BusbudError.BusbudError>(
  bb_err: T,
  cause_err: Error | undefined
): T {
  if (cause_err instanceof Error) {
    return bb_err.causedBy(cause_err);
  }

  bb_err.cause = cause_err;

  return bb_err;
}

/**
 * Convert from an Axios error (non-2xx response code)
 * that contains the error in the response body
 *
 * @param err
 * @returns
 */
function fromAxiosError(
  err: AxiosError<{ error?: unknown }>
): BusbudError.BusbudError {
  const { response } = err;
  if (!response) {
    return new BusbudError.GeneralError().causedBy(err);
  }

  if (response.status >= 500) {
    return new BusbudError.CannotReachAPI().causedBy(err);
  }

  if (typeof response.data === "string" || !response.data?.error) {
    return new BusbudError.GeneralError().causedBy(err);
  }

  return fromPlainObject(response.data.error as Error, err);
}

/**
 * Useful if we already extracted the error object from the response body,
 * for example in the case of a 200 response
 *
 * @param error_object
 * @param [cause]
 * @returns
 */
function fromPlainObject(
  error_object: Error & {
    type?: string;
    additional_data?: unknown;
    errors?: unknown;
    transaction?: unknown;
  },
  cause?: Error | undefined
): BusbudError.BusbudError {
  let bb_err;

  if (
    error_object.type &&
    BusbudError[error_object.type as keyof typeof BusbudError]
  ) {
    bb_err = safeCausedBy(
      new (BusbudError[error_object.type as keyof typeof BusbudError] as any)(),
      cause || error_object
    );
  } else {
    bb_err = safeCausedBy(
      new BusbudError.GeneralError(),
      cause || error_object
    );
  }
  bb_err.additional_data = error_object.additional_data;
  bb_err.errors = error_object.errors;
  bb_err.transaction = error_object.transaction
    ? error_object.transaction
    : undefined;

  return bb_err;
}

export function toUserVisibleError(
  err: Error | null
): BusbudError.BusbudError | null {
  if (!err) {
    return null;
  }

  var bb_err = toBusbudError(err);
  if (bb_err && bb_err.userVisible) {
    return bb_err;
  }

  return safeCausedBy(new BusbudError.GeneralError(), err);
}

/**
 * Useful for tracking purposes. We typically track the error shown to the user, which can often be generic. It helps
 * to know the cause behind it.
 *
 * @param err
 * @returns
 */
export function getRootError(err: Error | null): ErrorWithCause | null {
  return (
    (err as ErrorWithCause)?.rootCause ??
    (err as ErrorWithCause)?.cause ??
    (err as ErrorWithCause) ??
    null
  );
}

/**
 * Useful for tracking purposes. Extracts the root_cause error name, sent by NAPI
 * to help track the root cause of the error.
 *
 * @param err
 * @returns
 */
function getRootCauseErrorNameFromNAPI(
  err:
    | ((ErrorWithCause | AxiosErrorWithCause) & {
        rootCause?: ErrorWithCause | AxiosErrorWithCause;
        cause?: ErrorWithCause | AxiosErrorWithCause;
      })
    | null
): string | null {
  if (!err) {
    return null;
  }

  let response: AxiosResponse | undefined;

  if (err.rootCause && "response" in err.rootCause && err.rootCause.response) {
    response = err.rootCause.response;
  } else if (err.cause && "response" in err.cause && err.cause.response) {
    response = err.cause.response;
  } else if ("response" in err && err.response) {
    response = err.response;
  }

  if (response && response.data && response.data.error) {
    return response.data.error.root_cause;
  }

  return null;
}

/**
 * Useful for tracking purposes. We typically track the error shown to the user, which can often be generic. It helps
 * to know the cause behind it.
 * If no root errors are available, or if it's the same as the main error, tries to use the root_cause error name
 * from NAPI's response.
 *
 * @param err
 * @returns
 */
export function getRootErrorName(err: Error | null): string | null {
  if (!err) {
    return null;
  }

  const root_error_name = getErrorName(getRootError(err) ?? null);
  const error_name = getErrorName(err);
  const root_cause_error_from_napi = getRootCauseErrorNameFromNAPI(
    err as ErrorWithCause
  );

  if (root_error_name && root_error_name !== error_name) {
    return root_error_name;
  }

  return root_cause_error_from_napi || root_error_name || null;
}

/**
 * @param err
 * @returns
 */
export function getErrorName(
  err: (Error & { type?: string; error_type?: string }) | null | undefined
): string | undefined {
  if (!err) {
    return;
  }

  if (isBusbudError(err)) {
    return err.name;
  } else if (err.type && err.error_type) {
    // altered format of BusbudError that came from Napi response
    return err.type;
  } else if (err instanceof SuperError) {
    return err.name;
  } else {
    const api_err = toMaybeApiError(err);
    return api_err ? api_err.name : undefined;
  }
}

export function getErrorModalConfig(
  translator: TranslatorService,
  sentry: SentryClient | null,
  err: BusbudError.BusbudError | null,
  locale: SupportedLocale
): ErrorModalConfig {
  let error_type = err && err.name;
  const top_level_error = error_type;
  if (
    (!error_type || error_type === "GeneralError") &&
    err &&
    err.cause &&
    err.cause.name
  ) {
    error_type = err.cause.name;
  }

  switch (error_type) {
    case "AncillaryItemUnavailable":
      return {
        name: "AncillaryItemUnavailable",
        title: translator.t("!error.AncillaryItemUnavailable.title"),
        text: translator.t("!error.AncillaryItemUnavailable.text"),
        button_config: {
          secondary_button: {
            label: translator.t("!error.AncillaryItemUnavailable.button"),
            action_name: ERROR_HANDLING_ACTIONS.RESET_ANCILLARY_ITEMS
          }
        }
      };
    case "BadRequest":
      return {
        name: "BadRequest",
        title: translator.t("!error.BadRequest.title"),
        text: translator.t("!error.BadRequest.text", {
          support_url: helpUrl(locale)
        })
      };
    case "BookingDepartureExpired":
      return {
        name: "BookingDepartureExpired",
        title: translator.t("!error.BookingDepartureExpired.title"),
        text: translator.t("!error.BookingDepartureExpired.text")
      };
    case "BookingDepartureNoLongerAvailable":
      return {
        name: "BookingDepartureNoLongerAvailable",
        title: translator.t("!error.BookingDepartureNoLongerAvailable.title"),
        text: translator.t("!error.BookingDepartureNoLongerAvailable.text")
      };
    case "CartDepartureExpired":
      return {
        name: "CartDepartureExpired",
        title: translator.t("!error.CartDepartureExpired.title"),
        text: translator.t("!error.CartDepartureExpired.text")
      };
    case "CartDepartureNoLongerAvailable":
      return {
        name: "CartDepartureNoLongerAvailable",
        title: translator.t("!error.CartDepartureNoLongerAvailable.title"),
        text: translator.t("!error.CartDepartureNoLongerAvailable.text")
      };
    case "CartRoundtripNotApplicable":
      return {
        name: "CartRoundtripNotApplicable",
        title: translator.t("!error.CartRoundtripNotApplicable.title"),
        text: translator.t("!error.CartRoundtripNotApplicable.text", {
          support_url: helpUrl(locale)
        })
      };
    case "CreditCardDeniedExternal":
      return {
        name: "CreditCardDeniedExternal",
        title: translator.t("!error.CreditCardDeniedExternal.title"),
        text: translator.t("!error.CreditCardDeniedExternal.text"),
        button_config: {
          cta_button: {
            label: translator.t("!error.CreditCardDeniedExternal.button.cta"),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_CHANGE_CREDIT_CARD
          },
          secondary_button: {
            label: translator.t(
              "!error.CreditCardDeniedExternal.button.secondary"
            ),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_MORE_INFO
          }
        }
      };
    case "CreditNotAllowed":
      return {
        name: "CreditNotAllowed",
        title: translator.t("!error.CreditNotAllowed.title"),
        text: translator.t("!error.CreditNotAllowed.text")
      };
    case "DecodeError":
      return {
        name: "DecodeError",
        title: translator.t("!error.GeneralError.title"),
        text: translator.t("!error.GeneralError.text"),
        button_config: {
          cta_button: {
            label: translator.t("!error.DecodeError.button.cta"),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_RESTART_SEARCH
          },
          secondary_button: {
            label: translator.t("!error.DecodeError.button.primary"),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_TRY_LATER
          }
        }
      };
    case "DepartureNotFound":
      return {
        name: "DepartureNotFound",
        title: translator.t("!error.DepartureNotFound.title"),
        text: translator.t("!error.DepartureNotFound.text"),
        button_config: {
          cta_button: {
            label: translator.t("!error.DepartureNotFound.button.cta"),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_RESTART_SEARCH
          }
        }
      };
    case "DepartureNotYetBookable":
      return {
        name: "DepartureNotFound",
        title: translator.t("!error.DepartureNotYetBookable.title"),
        text: translator.t("!error.DepartureNotYetBookable.text")
      };
    case "DiscountCodeInvalidUser":
      return {
        name: "DiscountCodeInvalidUser",
        title: translator.t("!error.DiscountCodeInvalidUser.title"),
        text: translator.t("!error.DiscountCodeInvalidUser.text"),
        button_config: {
          cta_button: {
            label: translator.t("!route.errors.default-button-label"),
            action_name: ERROR_HANDLING_ACTIONS.RESET_DISCOUNT_CODE
          }
        }
      };
    case "EmailUserExists":
      return {
        name: "EmailUserExists",
        title: translator.t("!error.EmailUserExists.title"),
        text: translator.t("!error.EmailUserExists.text")
      };
    case "FBUserExists":
      return {
        name: "FBUserExists",
        title: translator.t("!error.FBUserExists.title"),
        text: translator.t("!error.FBUserExists.text")
      };
    case "GeneralError":
      return {
        name: "GeneralError",
        title: translator.t("!error.GeneralError.title"),
        text: translator.t("!error.GeneralError.text")
      };
    case "GoogleUserExists":
      return {
        name: "GoogleUserExists",
        title: translator.t("!error.GoogleUserExists.title"),
        text: translator.t("!error.GoogleUserExists.text")
      };
    case "AppleUserExists":
      return {
        name: "AppleUserExists",
        title: translator.t("!error.AppleUserExists.title"),
        text: translator.t("!error.AppleUserExists.text")
      };
    case "GovernmentIdRequired":
      return {
        name: "GovernmentIdRequired",
        title: translator.t("!error.GovernmentIdRequired.title"),
        text: translator.t("!error.GovernmentIdRequired.text")
      };
    case "ImpossibleRoundtrip":
      return {
        name: "ImpossibleRoundtrip",
        title: translator.t("!error.ImpossibleRoundtrip.title"),
        text: translator.t("!error.ImpossibleRoundtrip.text")
      };
    case "InputInvalid":
      return {
        name: "InputInvalid",
        title: translator.t("!error.InputInvalid.title"),
        text: translator.t("!error.InputInvalid.text")
      };
    case "InputMissing":
      return {
        name: "InputMissing",
        title: translator.t("!error.InputMissing.title"),
        text: translator.t("!error.InputMissing.text")
      };
    case "InsufficientCredits":
      return {
        name: "InsufficientCredits",
        title: translator.t("!error.InsufficientCredits.title"),
        text: translator.t("!error.InsufficientCredits.text"),
        button_config: {
          cta_button: {
            label: translator.t("!route.errors.default-button-label"),
            action_name: ERROR_HANDLING_ACTIONS.RESET_REDEEMED_AMOUNT
          }
        }
      };
    case "InstallmentsNotSupported":
      return {
        name: "InstallmentsNotSupported",
        title: translator.t("!error.InstallmentsNotSupported.title"),
        text: translator.t("!error.InstallmentsNotSupported.text")
      };
    case "InvalidCode":
      return {
        name: "InvalidCode",
        title: translator.t("!error.InvalidCode.title"),
        text: translator.t("!error.InvalidCode.text")
      };
    case "InvalidPhoneNumber":
      return {
        name: "InvalidPhoneNumber",
        title: translator.t("!error.InvalidPhoneNumber.title"),
        text: translator.t("!error.InvalidPhoneNumber.text")
      };
    case "InvalidPrice":
      return {
        name: "InvalidPrice",
        title: translator.t("!error.InvalidPrice.title"),
        text: translator.t("!error.InvalidPrice.text")
      };
    case "InvalidSeatSelection":
      return {
        name: "InvalidSeatSelection",
        title: translator.t("!error.SelectedSeatsNotAvailable.title"),
        text: translator.t("!error.SelectedSeatsNotAvailable.text"),
        tip: translator.t("!error.tip.PreAuthorizationRelease.text"),
        button_config: {
          primary_button: {
            label: translator.t(
              "!error.SelectedSeatsNotAvailable.button.continue"
            ),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_UPDATE_SEAT
          }
        }
      };
    case "PassengerInvalidSeatId":
      return {
        name: "PassengerInvalidSeatId",
        title: translator.t("!error.SelectedSeatsNotAvailable.title"),
        text: translator.t("!error.SelectedSeatsNotAvailable.text"),
        tip: translator.t("!error.tip.PreAuthorizationRelease.text"),
        button_config: {
          primary_button: {
            label: translator.t(
              "!error.SelectedSeatsNotAvailable.button.continue"
            ),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_UPDATE_SEAT
          }
        }
      };
    case "PartnerLoyaltyProgramError":
      return {
        name: "PartnerLoyaltyProgramError",
        title: translator.t("!error.PartnerLoyaltyProgramError.title"),
        text: translator.t("!error.PartnerLoyaltyProgramError.text"),
        button_config: {
          cta_button: {
            label: translator.t("!error.PartnerLoyaltyProgramError.button"),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_UPDATE_LOYALTY
          }
        }
      };
    case "SelectedSeatsNotAvailable":
      return {
        name: "SelectedSeatsNotAvailable",
        title: translator.t("!error.SelectedSeatsNotAvailable.title"),
        text: translator.t("!error.SelectedSeatsNotAvailable.text"),
        tip: translator.t("!error.tip.PreAuthorizationRelease.text"),
        button_config: {
          primary_button: {
            label: translator.t(
              "!error.SelectedSeatsNotAvailable.button.continue"
            ),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_UPDATE_SEAT
          }
        }
      };
    case "PassengerAnswersInvalid":
      return _getPassengerAnswersInvalidModalConfig(translator, err, {
        name: "CouldNotBookTicketsForAllPassengers",
        title: translator.t("!error.CouldNotBookTicketsForAllPassengers.title"),
        text: translator.t("!error.CouldNotBookTicketsForAllPassengers.text"),
        button_config: {
          cta_button: {
            label: translator.t(
              "!error.CouldNotBookTicketsForAllPassengers.button.cta"
            ),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_RESTART_SEARCH
          }
        }
      });
    case "CouldNotBookTicketsForAllPassengers":
      return _getPassengerAnswersInvalidModalConfig(translator, err, {
        name: "CouldNotBookTicketsForAllPassengers",
        title: translator.t("!error.CouldNotBookTicketsForAllPassengers.title"),
        text: translator.t("!error.CouldNotBookTicketsForAllPassengers.text"),
        button_config: {
          cta_button: {
            label: translator.t(
              "!error.CouldNotBookTicketsForAllPassengers.button.cta"
            ),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_RESTART_SEARCH
          },
          secondary_button: {
            label: translator.t(
              "!error.CouldNotBookTicketsForAllPassengers.button.primary"
            ),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_TRY_LATER
          }
        }
      });
    case "NetworkError":
      return {
        name: "NetworkError",
        title: translator.t("!error.GeneralError.title"),
        text: translator.t("!error.GeneralError.text"),
        button_config: {
          cta_button: {
            label: translator.t("!error.NetworkError.button.cta"),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_RESTART_SEARCH
          },
          secondary_button: {
            label: translator.t("!error.NetworkError.button.primary"),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_TRY_LATER
          }
        }
      };
    case "NoSeatsAvailable":
      return {
        name: "NoSeatsAvailable",
        title: translator.t("!error.NoSeatsAvailable.title"),
        text: translator.t("!error.NoSeatsAvailable.text"),
        tip: translator.t("!error.tip.PreAuthorizationRelease.text"),
        button_config: {
          cta_button: {
            label: translator.t("!error.GeneralError.button.cta"),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_RESTART_SEARCH
          }
        }
      };
    case "NotEnoughSeatsForAllPassengers":
      return {
        name: "NotEnoughSeatsForAllPassengers",
        title: translator.t("!error.NotEnoughSeatsForAllPassengers.title"),
        text: translator.t("!error.NotEnoughSeatsForAllPassengers.text"),
        tip: translator.t("!error.tip.PreAuthorizationRelease.text"),
        button_config: {
          primary_button: {
            label: translator.t(
              "!error.NotEnoughSeatsForAllPassengers.button.primary"
            ),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_UPDATE_SEAT
          }
        }
      };
    case "NoUser":
      return {
        name: "NoUser",
        title: translator.t("!error.NoUser.title"),
        text: translator.t("!error.NoUser.text")
      };
    case "OperationalError":
      return {
        name: "OperationalError",
        title: translator.t("!error.OperationalError.title"),
        text: translator.t("!error.OperationalError.text"),
        button_config: {
          cta_button: {
            label: translator.t("!error.OperationalError.button.cta"),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_RESTART_SEARCH
          },
          secondary_button: {
            label: translator.t("!error.OperationalError.button.primary"),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_TRY_LATER
          }
        }
      };
    case "PassengerAnswersNotUnique":
      return {
        name: "PassengerAnswersNotUnique",
        title: translator.t("!error.PassengerAnswersNotUnique.title"),
        text: translator.t("!error.PassengerAnswersNotUnique.text")
      };
    case "PassengerMissingInformation":
      return {
        name: "PassengerMissingInformation",
        title: translator.t("!error.PassengerMissingInformation.title"),
        text: translator.t("!error.PassengerMissingInformation.text")
      };
    case "PassengerQuestionsUnanswered":
      return {
        name: "PassengerQuestionsUnanswered",
        title: translator.t("!error.PassengerQuestionsUnanswered.title"),
        text: translator.t("!error.PassengerQuestionsUnanswered.text")
      };
    case "PasswordInvalid":
      return {
        name: "PasswordInvalid",
        title: translator.t("!error.PasswordInvalid.title"),
        text: translator.t("!error.PasswordInvalid.text")
      };
    case "PasswordResetTokenInvalid":
      return {
        name: "PasswordResetTokenInvalid",
        title: translator.t("!error.PasswordResetTokenInvalid.title"),
        text: translator.t("!error.PasswordResetTokenInvalid.text")
      };
    case "PastBuyCutoff":
      return {
        name: "PastBuyCutoff",
        title: translator.t("!error.PastBuyCutoff.title"),
        text: translator.t("!error.PastBuyCutoff.text")
      };
    case "PurchaseAbandoned":
      return {
        name: "PurchaseAbandoned",
        title: translator.t("!error.PurchaseAbandoned.title"),
        text: translator.t("!error.PurchaseAbandoned.text"),
        button_config: {
          cta_button: {
            label: translator.t("!error.PurchaseAbandoned.button.cta"),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_CHANGE_CREDIT_CARD
          }
        }
      };
    case "RoundtripNotSupported":
      return {
        name: "RoundtripNotSupported",
        title: translator.t("!error.RoundtripNotSupported.title"),
        text: translator.t("!error.RoundtripNotSupported.text")
      };
    case "SignInWithEmail":
      return {
        name: "SignInWithEmail",
        title: translator.t("!error.SignInWithEmail.title"),
        text: translator.t("!error.SignInWithEmail.text")
      };
    case "SpreedlyTokenError":
      return {
        name: "SpreedlyTokenError",
        title: translator.t("!error.SpreedlyTokenError.title"),
        text: translator.t("!error.SpreedlyTokenError.text")
      };
    case "TicketGeneratingPollLimitReached":
      return {
        name: "TicketGeneratingPollLimitReached",
        title: translator.t("!error.TicketGeneratingPollLimitReached.title"),
        text: translator.t("!error.TicketGeneratingPollLimitReached.text")
      };
    case "TicketNotCancellable":
      return {
        name: "TicketNotCancellable",
        title: translator.t("!error.TicketNotCancellable.title"),
        text: translator.t("!error.TicketNotCancellable.text", {
          support_url: helpUrl(locale)
        })
      };
    case "TicketTypeNotAvailable":
      return {
        name: "TicketTypeNotAvailable",
        title: translator.t("!error.TicketTypeNotAvailable.title"),
        text: translator.t("!error.TicketTypeNotAvailable.text", {
          support_url: helpUrl(locale)
        })
      };
    case "TransactionCardInfoIncorrect":
      return {
        name: "TransactionCardInfoIncorrect",
        title: translator.t("!error.TransactionCardInfoIncorrect.title"),
        text: translator.t("!error.TransactionCardInfoIncorrect.text")
      };
    case "InsufficientBalance":
      return {
        name: "InsufficientBalance",
        title: translator.t("!error.InsufficientBalance.title"),
        text: translator.t("!error.InsufficientBalance.text")
      };
    case "TransactionCardNotSupported":
      return {
        name: "TransactionCardNotSupported",
        title: translator.t("!error.TransactionCardNotSupported.title"),
        text: translator.t("!error.TransactionCardNotSupported.text")
      };
    case "TransactionContactSupport":
      return {
        name: "TransactionContactSupport",
        title: translator.t("!error.TransactionContactSupport.title"),
        text: translator.t("!error.TransactionContactSupport.text"),
        button_config: {
          cta_button: {
            label: translator.t("!error.TransactionContactSupport.button.cta"),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_CHANGE_CREDIT_CARD
          },
          secondary_button: {
            label: translator.t(
              "!error.TransactionContactSupport.button.secondary"
            ),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_MORE_INFO
          }
        }
      };
    case "TransactionDeclined":
      return {
        name: "TransactionDeclined",
        title: translator.t("!error.TransactionDeclined.title"),
        text: translator.t("!error.TransactionDeclined.text"),
        button_config: {
          cta_button: {
            label: translator.t("!error.TransactionFailed.button.cta"),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_CHANGE_CREDIT_CARD
          }
        }
      };
    case "TransactionDuplicate":
      return {
        name: "TransactionDuplicate",
        title: translator.t("!error.TransactionDuplicate.title"),
        text: translator.t("!error.TransactionDuplicate.text")
      };
    case "TransactionTimeout":
      return {
        name: "TransactionTimeout",
        title: translator.t("!error.TransactionTimeout.title"),
        text: translator.t("!error.TransactionTimeout.text")
      };
    case "TransactionVelocity":
      return {
        name: "TransactionVelocity",
        title: translator.t("!error.TransactionVelocity.title"),
        text: translator.t("!error.TransactionVelocity.text")
      };
    case "TransactionFailed":
      return {
        name: "TransactionFailed",
        title: translator.t("!error.TransactionFailed.title"),
        text: translator.t("!error.TransactionFailed.text"),
        button_config: {
          cta_button: {
            label: translator.t("!error.TransactionFailed.button.cta"),
            action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_CHANGE_CREDIT_CARD
          }
        }
      };
    case "TurkishIDValidationError":
      return {
        name: "TurkishIDValidationError",
        title: translator.t("!error.TurkishIDValidationError.title"),
        text: translator.t("!error.TurkishIDValidationError.text")
      };
    case "UnaccompaniedMinorNotAllowed":
      return {
        name: "UnaccompaniedMinorNotAllowed",
        title: translator.t("!error.UnaccompaniedMinorNotAllowed.title"),
        text: translator.t("!error.UnaccompaniedMinorNotAllowed.text")
      };
    case "Unauthorized":
      return {
        name: "Unauthorized",
        title: translator.t("!error.Unauthorized.title"),
        text: translator.t("!error.Unauthorized.text", {
          support_url: helpUrl(locale)
        })
      };
    case "UserAlreadyVerified":
      return {
        name: "UserAlreadyVerified",
        title: translator.t("!error.UserAlreadyVerified.title"),
        text: translator.t("!error.UserAlreadyVerified.text")
      };
    case "UserError":
      return {
        name: "UserError",
        title: translator.t("!error.UserError.title"),
        text: translator.t("!error.UserError.text", {
          support_url: helpUrl(locale)
        })
      };
    case "UserNotVerified":
      return {
        name: "UserNotVerified",
        title: translator.t("!error.UserNotVerified.title"),
        text: translator.t("!error.UserNotVerified.text")
      };
    case "UserVerificationTokenInvalid":
      return {
        name: "UserVerificationTokenInvalid",
        title: translator.t("!error.UserVerificationTokenInvalid.title"),
        text: translator.t("!error.UserVerificationTokenInvalid.text")
      };
    case "WheelchairNotAvailable":
      return {
        name: "WheelchairNotAvailable",
        title: translator.t("!error.WheelchairNotAvailable.title"),
        text: translator.t("!error.WheelchairNotAvailable.text")
      };
    default:
      if (top_level_error === "GeneralError") {
        return _getGeneralErrorModalConfig(translator);
      }

      if (sentry) {
        sentry.captureException(
          new Error(`No copy found for '${error_type}'`),
          {
            tags: {
              busbud_error: err && err.name,
              error_type
            }
          }
        );
      }

      return _getGeneralErrorModalConfig(translator);
  }
}

function _getPassengerAnswersInvalidModalConfig(
  translator: TranslatorService,
  err: BusbudError.BusbudError | null,
  fallback_error_config?: ErrorModalConfig
): ErrorModalConfig {
  let errors: ErrorWithQuestionKey[] = [];
  if (err) {
    const root_error = getRootError(err);
    if (root_error?.cause && Array.isArray(root_error.cause.errors)) {
      errors = root_error.cause.errors as ErrorWithQuestionKey[];
    } else if (root_error && Array.isArray(root_error.errors)) {
      errors = root_error.errors as ErrorWithQuestionKey[];
    }
  }

  const is_hes_error = errors.some(
    error => error.question_key === "tr_hes_code"
  );

  if (is_hes_error) {
    return {
      name: "PassengerAnswersInvalid",
      title: translator.t("!error.PassengerAnswersInvalid.hes.title"),
      text: translator.t("!error.PassengerAnswersInvalid.hes.text"),
      button_config: {
        cta_button: {
          label: translator.t(
            "!error.PassengerAnswersInvalid.hes.button.rectify"
          ),
          action_name: ERROR_HANDLING_ACTIONS.CHECKOUT_UPDATE_HES
        }
      }
    };
  }

  if (fallback_error_config) {
    return fallback_error_config;
  }

  return _getGeneralErrorModalConfig(translator);
}

function _getGeneralErrorModalConfig(
  translator: TranslatorService
): ErrorModalConfig {
  return {
    name: "GeneralError",
    title: translator.t("!error.GeneralError.title"),
    text: translator.t("!error.GeneralError.text")
  };
}

export const isDiscountInvalidError = (err: unknown) => {
  return (
    err instanceof BusbudError.DiscountCodeInvalidCityPair ||
    err instanceof BusbudError.DiscountCodeInvalidCountry ||
    err instanceof BusbudError.DiscountCodeInvalidUserExternal ||
    err instanceof BusbudError.DiscountCodeInvalid ||
    err instanceof BusbudError.DiscountCodeInvalidUser
  );
};
